import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../utils/authFetch";

const API_URL = process.env.REACT_APP_API_URL || "";

const EMPTY_FORM = {
  id: null,
  name: "",
  description: "",
  required_points: "",
  estimated_cost: "5.00",
  active: true,
  product_ids: [],
};

export default function GestionePremiPage() {
  const navigate = useNavigate();

  const [rewards, setRewards] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const isEditing = Boolean(form.id);

  const loadRewards = async () => {
    const res = await authFetch(`${API_URL}/api/rewards/manage/`);
    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(data?.detail || "Impossibile caricare i premi.");
    }

    setRewards(Array.isArray(data?.rewards) ? data.rewards : []);
  };

  const loadProducts = async () => {
    const res = await authFetch(
      `${API_URL}/api/prodotti/?tipo_listino=cliente`
    );
    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(data?.detail || "Impossibile caricare i prodotti.");
    }

    const list = Array.isArray(data)
      ? data
      : data?.results || data?.prodotti || [];

    setProducts(Array.isArray(list) ? list : []);
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");

      try {
        await Promise.all([loadRewards(), loadProducts()]);
      } catch (e) {
        console.error(e);
        setError(e.message || "Errore durante il caricamento.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const filteredProducts = useMemo(() => {
    const term = search.trim().toLowerCase();

    const list = products.filter((product) => {
      if (!term) return true;

      const haystack = [
        product?.name,
        product?.category,
        product?.code,
        product?.sku,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(term);
    });

    return list.sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""), "it")
    );
  }, [products, search]);

  const selectedProducts = useMemo(() => {
    const selected = new Set(form.product_ids.map(Number));

    return products
      .filter((product) => selected.has(Number(product.id)))
      .sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || ""), "it")
      );
  }, [products, form.product_ids]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setSearch("");
    setError("");
    setSuccess("");
  };

  const editReward = (reward) => {
    setForm({
      id: reward.id,
      name: reward.name || "",
      description: reward.description || "",
      required_points:
        reward.required_points != null
          ? String(reward.required_points)
          : "",
      estimated_cost:
        reward.estimated_cost != null
          ? String(reward.estimated_cost)
          : "5.00",
      active: Boolean(reward.active),
      product_ids: Array.isArray(reward.allowed_products)
        ? reward.allowed_products.map((product) => Number(product.id))
        : [],
    });

    setSearch("");
    setError("");
    setSuccess("");

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleProduct = (productId) => {
    const id = Number(productId);

    setForm((current) => {
      const selected = current.product_ids.includes(id);

      return {
        ...current,
        product_ids: selected
          ? current.product_ids.filter((item) => item !== id)
          : [...current.product_ids, id],
      };
    });
  };

  const removeProduct = (productId) => {
    const id = Number(productId);

    setForm((current) => ({
      ...current,
      product_ids: current.product_ids.filter((item) => item !== id),
    }));
  };

  const saveReward = async (event) => {
    event.preventDefault();

    setError("");
    setSuccess("");

    const name = form.name.trim();
    const description = form.description.trim();
    const requiredPoints = Number(form.required_points);
    const estimatedCost = Number(form.estimated_cost);

    if (!name) {
      setError("Inserisci il titolo del premio.");
      return;
    }

    if (
      !Number.isInteger(requiredPoints) ||
      requiredPoints <= 0
    ) {
      setError("Inserisci un numero di punti valido, maggiore di zero.");
      return;
    }

    if (
      !Number.isFinite(estimatedCost) ||
      estimatedCost < 0 ||
      !/^\d+(?:[.,]\d{1,2})?$/.test(String(form.estimated_cost).trim())
    ) {
      setError("Inserisci un costo interno valido, con massimo due decimali.");
      return;
    }

    if (form.product_ids.length === 0) {
      setError("Seleziona almeno un prodotto associato al premio.");
      return;
    }

    setSaving(true);

    try {
      const url = isEditing
        ? `${API_URL}/api/rewards/manage/${form.id}/`
        : `${API_URL}/api/rewards/manage/`;

      const method = isEditing ? "PUT" : "POST";

      const res = await authFetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name,
          description,
          required_points: requiredPoints,
          estimated_cost: estimatedCost,
          active: Boolean(form.active),
          product_ids: form.product_ids.map(Number),
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          data?.detail ||
            `Errore durante il salvataggio del premio (${res.status}).`
        );
      }

      await loadRewards();

      setSuccess(
        isEditing
          ? "Premio aggiornato correttamente."
          : "Premio creato correttamente."
      );

      setForm(EMPTY_FORM);
      setSearch("");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      console.error(e);
      setError(e.message || "Errore durante il salvataggio.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
        <div>
          <div className="text-muted small text-uppercase fw-semibold">
            Fidelity App Cliente
          </div>

          <h1 className="fw-bold mb-1">Gestione Premi</h1>

          <div className="text-muted">
            Crea e modifica i premi, i punti richiesti e i prodotti che
            possono essere consegnati.
          </div>
        </div>

        <button
          type="button"
          className="btn btn-outline-secondary rounded-pill px-4"
          onClick={() => navigate("/gestione-app-clienti")}
        >
          ← Gestione App Clienti
        </button>
      </div>

      {error && (
        <div className="alert alert-danger rounded-4">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success rounded-4">
          {success}
        </div>
      )}

      <div className="card border-0 shadow-sm rounded-4 mb-4">
        <div className="card-body p-4">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
            <div>
              <h4 className="fw-bold mb-1">
                {isEditing ? "Modifica premio" : "Nuovo premio"}
              </h4>

              <div className="text-muted">
                {isEditing
                  ? `Stai modificando il premio #${form.id}.`
                  : "Compila i dati per creare un nuovo premio."}
              </div>
            </div>

            {isEditing && (
              <button
                type="button"
                className="btn btn-outline-secondary rounded-pill"
                onClick={resetForm}
                disabled={saving}
              >
                Annulla modifica
              </button>
            )}
          </div>

          <form onSubmit={saveReward}>
            <div className="row g-3">
              <div className="col-12 col-lg-6">
                <label className="form-label fw-semibold">
                  Titolo premio
                </label>

                <input
                  type="text"
                  className="form-control form-control-lg"
                  value={form.name}
                  maxLength={100}
                  placeholder="Es. Bag in Box Cabernet Franc 5 litri"
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      name: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="col-12 col-md-6 col-lg-3">
                <label className="form-label fw-semibold">
                  Punti richiesti
                </label>

                <input
                  type="number"
                  min="1"
                  step="1"
                  className="form-control form-control-lg"
                  value={form.required_points}
                  placeholder="Es. 150"
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      required_points: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="col-12 col-md-6 col-lg-3">
                <label className="form-label fw-semibold">
                  Costo interno premio (EUR)
                </label>

                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="form-control form-control-lg"
                  value={form.estimated_cost}
                  placeholder="Es. 5,00"
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      estimated_cost: e.target.value,
                    }))
                  }
                />

                <div className="form-text">
                  Uso interno gestionale. Non visibile al cliente.
                </div>
              </div>

              <div className="col-12">
                <label className="form-label fw-semibold">
                  Descrizione
                </label>

                <textarea
                  className="form-control"
                  rows="3"
                  value={form.description}
                  placeholder="Descrizione mostrata al cliente nell'app."
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      description: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="col-12">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="reward-active"
                    checked={form.active}
                    onChange={(e) =>
                      setForm((current) => ({
                        ...current,
                        active: e.target.checked,
                      }))
                    }
                  />

                  <label
                    className="form-check-label fw-semibold"
                    htmlFor="reward-active"
                  >
                    Premio attivo e visibile nell'app
                  </label>
                </div>
              </div>
            </div>

            <hr className="my-4" />

            <div className="d-flex flex-wrap justify-content-between align-items-end gap-3 mb-3">
              <div>
                <h5 className="fw-bold mb-1">
                  Prodotti associati
                </h5>

                <div className="text-muted small">
                  Il punto vendita potrà consegnare soltanto uno dei prodotti
                  selezionati qui.
                </div>
              </div>

              <div
                className="badge text-bg-dark rounded-pill px-3 py-2"
                style={{ fontSize: 14 }}
              >
                {form.product_ids.length} selezionati
              </div>
            </div>

            {selectedProducts.length > 0 && (
              <div className="mb-3">
                <div className="fw-semibold small mb-2">
                  Selezionati
                </div>

                <div className="d-flex flex-wrap gap-2">
                  {selectedProducts.map((product) => (
                    <button
                      key={product.id}
                      type="button"
                      className="btn btn-sm btn-dark rounded-pill"
                      onClick={() => removeProduct(product.id)}
                      title="Rimuovi prodotto"
                    >
                      {product.name} ×
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-3">
              <input
                type="search"
                className="form-control"
                value={search}
                placeholder="Cerca prodotto per nome o categoria..."
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div
              className="border rounded-4 p-2"
              style={{
                maxHeight: 360,
                overflowY: "auto",
                background: "#fafafa",
              }}
            >
              {filteredProducts.length === 0 ? (
                <div className="text-muted text-center py-4">
                  Nessun prodotto trovato.
                </div>
              ) : (
                filteredProducts.map((product) => {
                  const selected = form.product_ids.includes(
                    Number(product.id)
                  );

                  return (
                    <label
                      key={product.id}
                      className="d-flex align-items-start gap-3 p-3 border-bottom"
                      style={{
                        cursor: "pointer",
                        background: selected ? "#eef8f2" : "transparent",
                      }}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input mt-1"
                        checked={selected}
                        onChange={() => toggleProduct(product.id)}
                      />

                      <div className="flex-grow-1">
                        <div className="fw-semibold">
                          {product.name}
                        </div>

                        <div className="text-muted small">
                          {product.category || "Senza categoria"}
                          {product.id ? ` · ID ${product.id}` : ""}
                        </div>
                      </div>
                    </label>
                  );
                })
              )}
            </div>

            <div className="d-flex flex-wrap gap-2 mt-4">
              <button
                type="submit"
                className="btn btn-success rounded-pill px-4"
                disabled={saving}
              >
                {saving
                  ? "Salvataggio..."
                  : isEditing
                  ? "Salva modifiche"
                  : "Crea premio"}
              </button>

              {isEditing && (
                <button
                  type="button"
                  className="btn btn-outline-secondary rounded-pill px-4"
                  onClick={resetForm}
                  disabled={saving}
                >
                  Annulla
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="fw-bold mb-0">Premi configurati</h4>

        <span className="text-muted">
          {rewards.length} premi
        </span>
      </div>

      {loading ? (
        <div className="card border-0 shadow-sm rounded-4">
          <div className="card-body p-5 text-center text-muted">
            Caricamento premi...
          </div>
        </div>
      ) : rewards.length === 0 ? (
        <div className="card border-0 shadow-sm rounded-4">
          <div className="card-body p-5 text-center">
            <div className="fw-bold mb-1">
              Nessun premio configurato
            </div>
            <div className="text-muted">
              Crea il primo premio utilizzando il modulo qui sopra.
            </div>
          </div>
        </div>
      ) : (
        <div className="row g-3">
          {rewards.map((reward) => (
            <div
              className="col-12 col-md-6 col-xl-4"
              key={reward.id}
            >
              <div
                className={`card h-100 border-0 shadow-sm rounded-4 ${
                  reward.active ? "" : "opacity-75"
                }`}
              >
                <div className="card-body p-4 d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                    <div>
                      <div className="text-muted small">
                        Premio #{reward.id}
                      </div>

                      <h5 className="fw-bold mb-1">
                        {reward.name}
                      </h5>
                    </div>

                    <span
                      className={`badge rounded-pill ${
                        reward.active
                          ? "text-bg-success"
                          : "text-bg-secondary"
                      }`}
                    >
                      {reward.active ? "Attivo" : "Non attivo"}
                    </span>
                  </div>

                  {reward.description && (
                    <div className="text-muted mb-3">
                      {reward.description}
                    </div>
                  )}

                  <div className="mb-2">
                    <span className="fw-bold fs-4">
                      {reward.required_points}
                    </span>{" "}
                    <span className="text-muted">punti</span>
                  </div>

                  <div className="text-muted small mb-2">
                    {reward.allowed_products?.length || 0} prodotti associati
                  </div>

                  <div className="small mb-4">
                    <span className="text-muted">Costo interno:</span>{" "}
                    <span className="fw-semibold">
                      EUR {Number(reward.estimated_cost || 0).toFixed(2)}
                    </span>
                  </div>

                  <button
                    type="button"
                    className="btn btn-outline-dark rounded-pill mt-auto"
                    onClick={() => editReward(reward)}
                  >
                    Modifica premio
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
